import { useState, useEffect } from 'react';
import { ArrowLeft, Award, Wine, Phone } from 'lucide-react';
import nhost from '../nhost';
import PointsBreakdown from './PointsBreakdown';

const MEMBER_QUERY = `
  query GetMemberProfile($userId: uuid!) {
    profiles_by_pk(id: $userId) {
      id display_name avatar_url created_at phone
    }
    ratings(where: { user_id: { _eq: $userId } }, order_by: { created_at: desc }) {
      id rating comment created_at
      wine {
        id name vintage region type grape
        session { id completed event { event_date } profile { display_name } }
      }
    }
    rsvp(where: { user_id: { _eq: $userId }, status: { _eq: "going" } }) {
      id
      event { id event_date sessions { id completed } }
    }
    event_hosts(where: { profile_id: { _eq: $userId } }) {
      event { sessions { id } }
    }
    user_points(where: { user_id: { _eq: $userId } }) { points }
    user_badges(where: { user_id: { _eq: $userId } }, order_by: { earned_at: asc }) {
      earned_at badge { id name description image_url }
    }
  }
`;

const cardStyle = {
  background: '#fff',
  border: '1px solid rgba(60,20,30,.08)',
  borderRadius: 16,
  padding: 16,
  marginBottom: 12,
};

const sectionTitle = {
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: 1.5,
  textTransform: 'uppercase',
  color: 'var(--muted)',
  marginBottom: 10,
};

const divider = { borderTop: '1px solid rgba(60,20,30,.07)' };

function Stars({ rating, size = 13 }) {
  return (
    <span style={{ position: 'relative', display: 'inline-block', fontSize: size, letterSpacing: 2, lineHeight: 1 }}>
      <span style={{ color: 'rgba(60,20,30,.18)' }}>★★★★★</span>
      <span style={{ position: 'absolute', left: 0, top: 0, overflow: 'hidden', whiteSpace: 'nowrap', color: 'var(--gold)', width: `${rating / 5 * 100}%` }}>★★★★★</span>
    </span>
  );
}

export default function MemberProfile({ userId, onClose, onWineClick, onGrapeClick }) {
  const [member, setMember] = useState(null);
  const [ratings, setRatings] = useState([]);
  const [rsvps, setRsvps] = useState([]);
  const [hostedCount, setHostedCount] = useState(0);
  const [totalPoints, setTotalPoints] = useState(null);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [loading, setLoading] = useState(true);
  const [memberBadges, setMemberBadges] = useState([]);
  const [activeTooltip, setActiveTooltip] = useState(null);

  function showBadgeTooltip(e, badge) {
    const rect = e.currentTarget.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    setActiveTooltip({
      id: badge.id,
      x: Math.max(8, Math.min(centerX - 80, window.innerWidth - 168)),
      bottom: window.innerHeight - rect.top + 8,
    });
  }

  useEffect(() => {
    nhost.graphql.request(MEMBER_QUERY, { userId }).then(({ data }) => {
      if (data) {
        setMember(data.profiles_by_pk);
        setRatings(data.ratings);
        setRsvps(data.rsvp);
        setHostedCount(data.event_hosts?.filter(h => h.event?.sessions?.length > 0).length || 0);
        setTotalPoints(data.user_points.reduce((a, b) => a + b.points, 0));
        setMemberBadges(data.user_badges);
      }
      setLoading(false);
    });
  }, [userId]);

  const totalRatings = ratings.length;
  const avgRating = totalRatings
    ? (ratings.reduce((a, b) => a + b.rating, 0) / totalRatings).toFixed(1)
    : '–';

  const highRated = ratings.filter(r => r.rating >= 4 && r.wine?.region);
  const regionCount = {};
  highRated.forEach(r => { const reg = r.wine.region; regionCount[reg] = (regionCount[reg] || 0) + 1; });
  const favoriteRegion = Object.entries(regionCount).sort((a, b) => b[1] - a[1])[0]?.[0] || '–';

  const grapeRated = ratings.filter(r => r.rating >= 4 && r.wine?.grape);
  const grapeCount = {};
  grapeRated.forEach(r => { const g = r.wine.grape; grapeCount[g] = (grapeCount[g] || 0) + 1; });
  const favoriteGrape = Object.entries(grapeCount).sort((a, b) => b[1] - a[1])[0]?.[0] || '–';

  const topWines = [...ratings]
    .sort((a, b) => b.rating - a.rating || new Date(b.created_at) - new Date(a.created_at))
    .slice(0, 5);

  const memberSince = member?.created_at
    ? new Date(member.created_at).toLocaleDateString('sv-SE', { year: 'numeric', month: 'long' })
    : null;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'var(--cream)', zIndex: 100, overflowY: 'auto' }}>
      <div style={{ maxWidth: 480, margin: '0 auto' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', padding: '10px 16px', background: 'var(--cream)', position: 'sticky', top: 0, zIndex: 10, borderBottom: '1px solid rgba(60,20,30,.06)' }}>
          <button
            onClick={onClose}
            style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--wine)', fontSize: 14, fontWeight: 500, padding: '6px 0' }}
          >
            <ArrowLeft size={18} strokeWidth={1.8} />
            Tillbaka
          </button>
        </div>

        <div style={{ padding: '16px 16px 100px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--muted)', fontSize: 13 }}>Laddar...</div>
          ) : !member ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--muted)', fontSize: 13 }}>Kunde inte hitta profilen.</div>
          ) : (
            <>
              {/* Avatar card */}
              <div style={{ ...cardStyle, textAlign: 'center', paddingTop: 28, paddingBottom: 24, borderRadius: 20 }}>
                <div style={{
                  width: 84, height: 84, borderRadius: '50%',
                  background: 'var(--wine-light)',
                  overflow: 'hidden',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  margin: '0 auto 12px',
                  border: '2px solid rgba(139,42,62,.12)',
                }}>
                  {member.avatar_url ? (
                    <img src={member.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <span style={{ fontSize: 34, color: 'var(--wine)', fontFamily: 'var(--font-display)', fontWeight: 600 }}>
                      {member.display_name?.[0]}
                    </span>
                  )}
                </div>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 600, color: 'var(--ink)' }}>{member.display_name}</div>
                {memberSince && <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 3 }}>Medlem sedan {memberSince}</div>}
                {member.phone && (
                  <a href={`tel:${member.phone}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13, color: 'var(--wine)', marginTop: 8, textDecoration: 'none', fontWeight: 500 }}>
                    <Phone size={13} strokeWidth={1.8} />
                    {member.phone}
                  </a>
                )}
              </div>

              {/* Points */}
              {totalPoints !== null && (
                <div style={{ ...cardStyle, background: 'var(--wine-light)', textAlign: 'center', borderRadius: 18, border: 'none' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 2 }}>
                    <Award size={16} color="var(--wine)" strokeWidth={1.7} />
                    <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--wine)' }}>Totala poäng</span>
                  </div>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: 40, fontWeight: 600, color: 'var(--wine)', lineHeight: 1.1 }}>{totalPoints}p</div>
                  <button
                    onClick={() => setShowBreakdown(true)}
                    style={{ marginTop: 10, fontSize: 13, color: 'var(--wine)', background: 'white', border: 'none', borderRadius: 20, padding: '7px 18px', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 500 }}
                  >
                    Visa aktiviteter →
                  </button>
                </div>
              )}
              {showBreakdown && (
                <PointsBreakdown
                  userId={userId}
                  displayName={member?.display_name}
                  totalPoints={totalPoints}
                  onClose={() => setShowBreakdown(false)}
                />
              )}

              {/* Badges */}
              {memberBadges.length > 0 && (
                <div style={cardStyle}>
                  <div style={sectionTitle}>Utmärkelser</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14 }}>
                    {memberBadges.map(ub => (
                      <div
                        key={ub.badge.id}
                        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 68, textAlign: 'center', cursor: 'pointer' }}
                        onMouseEnter={e => showBadgeTooltip(e, ub.badge)}
                        onMouseLeave={() => setActiveTooltip(null)}
                        onClick={e => activeTooltip?.id === ub.badge.id ? setActiveTooltip(null) : showBadgeTooltip(e, ub.badge)}
                      >
                        <div style={{ width: 52, height: 52, borderRadius: '50%', background: 'var(--wine-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', border: '1.5px solid rgba(139,42,62,.15)', marginBottom: 6 }}>
                          {ub.badge.image_url
                            ? <img src={ub.badge.image_url} alt={ub.badge.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            : <Award size={22} color="var(--wine)" strokeWidth={1.5} />
                          }
                        </div>
                        <div style={{ fontSize: 10, fontWeight: 500, color: 'var(--ink)', lineHeight: 1.3 }}>{ub.badge.name}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {activeTooltip && (() => {
                const ub = memberBadges.find(b => b.badge.id === activeTooltip.id);
                return ub?.badge.description ? (
                  <div style={{
                    position: 'fixed', left: activeTooltip.x, bottom: activeTooltip.bottom,
                    width: 160, background: 'white', border: '1px solid rgba(60,20,30,.08)',
                    borderRadius: 10, padding: '8px 10px', zIndex: 500,
                    boxShadow: '0 4px 16px rgba(60,20,30,.12)', pointerEvents: 'none',
                  }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--wine)', marginBottom: 4 }}>{ub.badge.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--muted)', lineHeight: 1.5 }}>{ub.badge.description}</div>
                  </div>
                ) : null;
              })()}

              {/* Statistik */}
              <div style={{ ...cardStyle, padding: '16px 16px 8px' }}>
                <div style={sectionTitle}>Statistik</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 8 }}>
                  {[
                    { num: totalRatings, label: 'Betygsatta viner' },
                    { num: avgRating, label: 'Snittbetyg' },
                    { num: hostedCount, label: 'Hållna provningar' },
                    { num: favoriteRegion !== '–' ? favoriteRegion.split(',')[0] : '–', label: 'Favoritregion' },
                  ].map(({ num, label }) => (
                    <div key={label} style={{ background: 'var(--cream)', borderRadius: 12, padding: '12px 14px', textAlign: 'center' }}>
                      <div style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 600, color: 'var(--wine)', lineHeight: 1 }}>{num}</div>
                      <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 4, fontWeight: 500 }}>{label}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Favoritdruva */}
              {favoriteGrape !== '–' && (
                <div style={{ ...cardStyle, cursor: 'pointer' }} onClick={() => onGrapeClick?.(favoriteGrape)}>
                  <div style={sectionTitle}>Favoritdruva</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 38, height: 38, borderRadius: '50%', background: 'var(--wine-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Wine size={18} color="var(--wine)" strokeWidth={1.7} />
                    </div>
                    <div style={{ fontFamily: 'var(--font-display)', fontSize: 17, fontWeight: 600, color: 'var(--wine)' }}>{favoriteGrape}</div>
                  </div>
                </div>
              )}

              {/* Topp 5 */}
              {topWines.length > 0 && (
                <div style={cardStyle}>
                  <div style={sectionTitle}>Topp 5 viner</div>
                  {topWines.map((r, i) => (
                    <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0', ...(i < topWines.length - 1 ? divider : {}), cursor: 'pointer' }} onClick={() => onWineClick?.(r.wine)}>
                      <div style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 300, color: 'var(--muted)', width: 22, textAlign: 'center', flexShrink: 0 }}>{i + 1}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 500 }}>{r.wine?.name}</div>
                        <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 1 }}>
                          {r.wine?.vintage ? `${r.wine.vintage} · ` : ''}{r.wine?.region}
                        </div>
                      </div>
                      <Stars rating={r.rating} size={13} />
                    </div>
                  ))}
                </div>
              )}

              {totalRatings === 0 && (
                <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--muted)' }}>
                  <Wine size={32} color="rgba(60,20,30,.2)" strokeWidth={1.5} style={{ marginBottom: 10 }} />
                  <div style={{ fontSize: 14 }}>{member.display_name} har inte betygsatt några viner än.</div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
